import React, { useEffect, useState } from 'react'
import { api } from '../../lib/api'
import { EmptyState } from '../../components/ui'
import { StatusBanner } from '../../components/StatusBanner'
import GroupPracticeDashboard from './GroupPracticeDashboard'
import type { PracticeMetrics, OrganizationHealth, BillingSummary } from '../../lib/types'

/** Clinic tier = everything Group Practice has, plus org-wide benchmarking
 * and billing — a clinic has many providers, so these sections use the
 * exact same endpoints as SoloProviderDashboard's Continuity/Performance,
 * just framed org-wide (per-provider comparison) instead of single-provider. */
export default function ClinicDashboard() {
  const [metrics, setMetrics] = useState<PracticeMetrics | null>(null)
  const [health, setHealth] = useState<OrganizationHealth | null>(null)
  const [billing, setBilling] = useState<BillingSummary | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    Promise.all([
      api('/v1/practice/metrics'),
      api('/v1/practice/health'),
      api('/v1/practice/billing-summary'),
    ]).then(([m, h, b]) => { setMetrics(m); setHealth(h); setBilling(b) }).finally(() => setLoading(false))
  }, [])

  return (
    <div>
      <GroupPracticeDashboard />

      <div className="section-block">
        <div className="section-head"><h2>Organization health</h2></div>
        {loading ? (
          <div className="page-loading">Loading…</div>
        ) : health && (
          <StatusBanner
            riskLevel={health.risk_level}
            score={health.health_score}
            components={[
              { label: 'Session 4 rate', value: health.components.session_4_rate != null ? `${Math.round(health.components.session_4_rate * 100)}%` : '—' },
              { label: 'Early dropout rate', value: health.components.early_dropout_rate != null ? `${Math.round(health.components.early_dropout_rate * 100)}%` : '—' },
              { label: 'Active providers', value: health.components.active_providers },
              { label: 'Open continuity alerts', value: health.components.continuity_alerts_open },
            ]}
          />
        )}
      </div>

      <div className="section-block">
        <div className="section-head"><h2>Provider benchmarking</h2></div>
        {!metrics || metrics.provider_retention.length === 0 ? (
          <EmptyState>No provider retention data yet.</EmptyState>
        ) : (
          <div className="table-wrap">
            <table className="data-table">
              <thead><tr><th>Provider</th><th>Total cases</th><th>Reached Session 4</th><th>Retention</th></tr></thead>
              <tbody>
                {[...metrics.provider_retention]
                  .sort((a, b) => (b.retention_rate ?? 0) - (a.retention_rate ?? 0))
                  .map((row) => (
                    <tr key={row.provider_id}>
                      <td>{row.provider_name}</td>
                      <td>{row.total_cases}</td>
                      <td>{row.reached_session_4}</td>
                      <td>{row.retention_rate != null ? `${Math.round(row.retention_rate * 100)}%` : '—'}</td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="section-block">
        <div className="section-head"><h2>Billing summary</h2></div>
        {!billing || billing.by_event_type.length === 0 ? (
          <EmptyState>No billing events recorded yet.</EmptyState>
        ) : (
          <div className="row-list">
            {billing.by_event_type.map((row) => (
              <div key={row.event_type} className="row">
                <span>{row.event_type.replace(/_/g, ' ')} ({row.count})</span>
                <span className="muted">${(row.total_amount_cents / 100).toFixed(2)}</span>
              </div>
            ))}
            <div className="row"><span style={{ fontWeight: 600 }}>Total</span><span style={{ fontWeight: 600 }}>${(billing.total_amount_cents / 100).toFixed(2)}</span></div>
          </div>
        )}
      </div>
    </div>
  )
}
